import { useCallback, useEffect, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/lib/checkins";
import { VersionDialog } from "./VersionDialog";

const BADGE = { draft: "bg-zinc-100 text-zinc-700", clinical_review: "bg-amber-100 text-amber-900",
  published: "bg-emerald-100 text-emerald-900", retired: "bg-zinc-200 text-zinc-500" };

export function TemplatesPanel({ isLead }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get("/checkin-templates").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  return (
    <div className="space-y-4" data-testid="checkin-templates">
      <p className="text-sm text-zinc-600">Templates hold the questions, scoring thresholds, safety rules and wording. Draft → clinical review → published → retired; only clinical leads can publish.</p>
      {rows.map((t) => (
        <Card key={t.id} className="p-4" data-testid={`template-${t.cohort_code}`}>
          <div className="flex items-baseline gap-2">
            <h3 className="font-display font-semibold text-zinc-950">{t.name}</h3>
            <span className="font-mono-num text-xs text-zinc-500">{t.cohort_code}</span>
          </div>
          <table className="mt-2 w-full text-sm">
            <tbody className="divide-y divide-zinc-100">
              {t.versions.map((v) => (
                <tr key={v.id} data-testid={`template-version-${v.id}`}>
                  <td className="py-1.5 font-mono-num">v{v.semantic_version}{v.id === t.current_version_id && <span className="ml-2 rounded bg-zinc-900 px-1.5 py-0.5 text-2xs font-semibold text-white">CURRENT</span>}</td>
                  <td><span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${BADGE[v.status]}`}>{v.status.replace("_", " ")}</span></td>
                  <td className="text-xs text-zinc-500">{v.published_at ? `published ${fmtDate(v.published_at)}` : `created ${fmtDate(v.created_at)}`}</td>
                  <td className="text-right whitespace-nowrap">
                    {v.status !== "retired" && (
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/progress-review/preview/${v.id}`, "_blank", "noopener")}
                        data-testid={`template-version-preview-${v.id}`} title="Open the client questionnaire and summary pages — nothing is saved">
                        <Eye className="mr-1 h-3.5 w-3.5" /> Preview client pages
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setOpen(v.id)} data-testid={`template-version-open-${v.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
      {open && <VersionDialog vid={open} isLead={isLead} onClose={() => { setOpen(null); load(); }} onChanged={load} />}
    </div>
  );
}
